import React, { useEffect, useState } from 'react';
import { Heading } from '../../../../../_lib/components/Heading/Heading';
import { murmurhash } from '../../../../../lib/js/hash';
import { Namespace } from '../../../../../translation/namespace';
import { tryTranslate } from '../../../../../lib/translation/utils';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { Row, Fieldset, RadioParam } from '../../../../../_lib/Form2';
import { CheckRadioForLists } from '../../../../../_lib/Form2';
import { PageType } from '../../../../../types/api/PageType';
import { PageTypeDescription } from '../../../../../_lib/components/PageType';
import { RequiredSymbol } from '../../../../../_lib/Form2/partials/RequiredSymbol/RequiredSymbol';

type CreatePageChoiceProps = {
  params: RadioParam<'pageType'>[];
  setSelectedPageTypeParameters: (param: RadioParam<'pageType'> | undefined) => void;
};

/** sort params either by its orderKey property, or alphabetically if all have an orderKey 0 */
const sortParams = (params: (RadioParam<'pageType'> | undefined)[]) => {
  const sortByOrderKey = params.every(p => p?.orderKey !== undefined) && params.some(p => p?.orderKey !== 0);

  return sortByOrderKey // @ts-expect-error TS cannot know here that we already checked this for `sortByOrderKey`
    ? params.sort((a, b) => (a.orderKey > b.orderKey ? 1 : -1))
    : params.sort((a, b) =>
        // we could make tryTranslate return an empty string instead of null?
        tryTranslate(`${Namespace.PageType}:${a?.value}.title`, null, '') >
        tryTranslate(`${Namespace.PageType}:${b?.value}.title`, null, '')
          ? 1
          : -1,
      );
};

export function CreatePageChoice({ params, setSelectedPageTypeParameters }: CreatePageChoiceProps) {
  const { t } = useSonTranslation('CreatePage');

  const [selectedPageType, setSelectedPageType] = useState<string>();

  /**
   * If there is only one radio in selection, this radio is checked automatically.
   */
  useEffect(() => {
    if (params.length === 1) {
      const param = params.at(0);
      setSelectedPageTypeParameters(param);
      setSelectedPageType(param?.value);
    }
  }, [params, setSelectedPageTypeParameters]);

  // Cluster params by navGroup to be able to order navGroups and params within separately
  // as well allows to render navGroups
  const fieldSets: Map<string, (RadioParam<'pageType'>)[]> = params.reduce(
    (acc, param) => {
      if (param.navGroup) {
        const group = acc.get(param.navGroup);

        if (group) {
          group.push(param);
        } else {
          acc.set(param.navGroup, [param]);
        }
      }

      return acc;
    }, new Map<string, (RadioParam<'pageType'>)[]>());

  return (
    <>
      <Heading hierarchy={3} className="create-page-form__heading">
        <>
          {t('steps.pageType.headline')}
          <RequiredSymbol />
        </>
      </Heading>
      <div className="create-page-form__selection">
        {fieldSets.entries().toArray().map(([key, value]) => (
          <Row key={key} modifier="block">
            {/* @ts-expect-error -- known issue from translation typing */}
            <Fieldset legend={t(`steps.fieldSet.${key}.legend`)}>
              {sortParams(value).map(param => (
                <CheckRadioForLists
                  key={murmurhash(`${key}-${param?.value}`)}
                  type="radio"
                  modifier="create-page"
                  pageTypeIconModifier="large-rounded"
                  /* @ts-expect-error -- known issue from translation typing */
                  label={t(`${Namespace.PageType}:${param.value}.title`)}
                  /* @ts-expect-error -- known issue from translation typing */
                  description={<PageTypeDescription pageType={param?.value} />}
                  param={param}
                  pageType={param?.value as PageType}
                  checked={selectedPageType === param?.value}
                  onChange={() => {
                    setSelectedPageTypeParameters(param);
                    setSelectedPageType(param?.value);
                  }}
                />
              ))}
            </Fieldset>
          </Row>
        ))}
      </div>
    </>
  );
}
